import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AgentConversation, AgentConversationsResponse } from '@Models';
import { bopsApi } from '@Network';

export const useAgentConversations = (sourceName?: string, limit = 20) => {
  const queryClient = useQueryClient();
  const queryKey = ['agent-conversations', limit, sourceName ?? null] as const;

  const query = useQuery<AgentConversationsResponse>({
    queryKey,
    queryFn: () => bopsApi.getAgentConversations(limit, sourceName),
    enabled: !!sourceName,
    staleTime: 30 * 1000, // 30 seconds
  });

  const addConversationToCache = useCallback(
    (newConversation: AgentConversation) => {
      queryClient.setQueryData<AgentConversationsResponse>(queryKey, (oldData) => {
        if (!oldData) return { conversations: [newConversation], total: 1 };

        return {
          conversations: [newConversation, ...oldData.conversations],
          total: oldData.total + 1,
        };
      });
    },
    [queryClient, queryKey],
  );

  const updateConversationInCache = useCallback(
    (conversationId: string, updates: Partial<AgentConversation>) => {
      queryClient.setQueryData<AgentConversationsResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          conversations: oldData.conversations.map((conversation) =>
            conversation.conversation_id === conversationId ? { ...conversation, ...updates } : conversation,
          ),
        };
      });
    },
    [queryClient, queryKey],
  );

  const removeConversationFromCache = useCallback(
    (conversationId: string) => {
      queryClient.setQueryData<AgentConversationsResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          conversations: oldData.conversations.filter(
            (conversation) => conversation.conversation_id !== conversationId,
          ),
          total: oldData.total - 1,
        };
      });

      // Also remove the conversation's interactions from cache
      queryClient.removeQueries({ queryKey: ['agent-conversation-interactions', conversationId] });
    },
    [queryClient, queryKey],
  );

  return {
    conversationsQuery: query,
    conversations: query.data?.conversations || [],
    total: query.data?.total || 0,
    isLoading: !sourceName || query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Cache manipulation methods
    addConversationToCache,
    updateConversationInCache,
    removeConversationFromCache,
  };
};

export default useAgentConversations;
